import React, { useState, useEffect } from 'react';
import { Search, Settings, Clock, Users, Plus, Edit2, Trash2, Eye } from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../api';
import Pagination from '../common/Pagination';
import EmptyState from '../common/EmptyState';
import Modal from '../Modal';
import FormInput from '../FormInput';
import ConfirmationModal from '../ConfirmationModal';

const TypesSoutenanceTab = () => {
  const [types, setTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [pagination, setPagination] = useState(null);

  // États pour les modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [detailType, setDetailType] = useState(null);
  const [formData, setFormData] = useState({
    nom: '',
    description: '',
    duree_minutes: '',
    nombre_jury_min: 3,
    nombre_jury_max: 7,
    niveau_requis: 'LICENCE'
  });
  const [formErrors, setFormErrors] = useState({});

  // États pour la confirmation de suppression
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [typeToDelete, setTypeToDelete] = useState(null);
  const [confirmConfig, setConfirmConfig] = useState({});

  const niveauxRequis = [
    { value: 'LICENCE', label: 'Licence' },
    { value: 'MASTER', label: 'Master' },
    { value: 'DOCTORAT', label: 'Doctorat' },
    { value: 'AUTRE', label: 'Autre' }
  ];

  const fetchTypes = async () => {
    setLoading(true);
    setError('');
    
    try {
      const queryParams = new URLSearchParams({
        page: currentPage,
        page_size: pageSize
      });
      
      if (searchTerm.trim()) {
        queryParams.append('search', searchTerm.trim());
      }
      
      const response = await api.get(`/soutenance/types-soutenance/?${queryParams}`);
      
      // Debug : log de la réponse complète
      console.log('Response complet:', response.data);
      
      if (response.data) {
        // Gestion de la structure de données personnalisée du backend
        let responseData = [];
        let paginationData = null;
        
        if (response.data.data) {
          // Structure avec pagination personnalisée
          responseData = response.data.data;
          paginationData = response.data.pagination;
          
          // Compléter les propriétés manquantes si nécessaire
          if (paginationData && !paginationData.has_next) {
            paginationData.has_next = paginationData.current_page < paginationData.total_pages;
          }
          if (paginationData && !paginationData.has_previous) {
            paginationData.has_previous = paginationData.current_page > 1;
          }
        } else if (response.data.results) {
          // Structure DRF standard
          responseData = response.data.results;
          paginationData = {
            count: response.data.count || 0,
            total_pages: Math.ceil((response.data.count || 0) / pageSize),
            current_page: currentPage,
            page_size: pageSize,
            has_next: !!response.data.next,
            has_previous: !!response.data.previous
          };
        } else {
          // Pas de pagination, toutes les données
          responseData = Array.isArray(response.data) ? response.data : [];
          paginationData = {
            count: responseData.length,
            total_pages: Math.ceil(responseData.length / pageSize),
            current_page: 1,
            page_size: pageSize,
            has_next: false,
            has_previous: false
          };
        }
        
        console.log('Données traitées:', responseData);
        console.log('Pagination traitée:', paginationData);
        
        setTypes(Array.isArray(responseData) ? responseData : []);
        setPagination(paginationData);
      }
    } catch (error) {
      console.error('Erreur lors du chargement:', error);
      setError('Erreur lors du chargement des types de soutenance');
      setTypes([]);
      setPagination(null);
      toast.error('Impossible de charger les types de soutenance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timeoutId = setTimeout(() => {
    fetchTypes();
    }, searchTerm ? 300 : 0);

    return () => clearTimeout(timeoutId);
  }, [currentPage, pageSize, searchTerm]);

  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  // Gestion du formulaire
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear l'erreur si l'utilisateur corrige le champ
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Ouvrir le modal de création/modification
  const handleOpenModal = (type = null) => {
    if (type) {
      setSelectedType(type);
      setFormData({
        nom: type.nom || '',
        description: type.description || '',
        duree_minutes: type.duree_minutes || '',
        nombre_jury_min: type.nombre_jury_min || 3,
        nombre_jury_max: type.nombre_jury_max || 7,
        niveau_requis: type.niveau_requis || 'LICENCE'
      });
    } else {
      setSelectedType(null);
    setFormData({
      nom: '',
      description: '',
      duree_minutes: '',
      nombre_jury_min: 3,
      nombre_jury_max: 7,
      niveau_requis: 'LICENCE'
    });
    }
    setFormErrors({});
    setIsModalOpen(true);
  };

  // Ouvrir le modal de détails
  const handleOpenDetailModal = (type) => {
    setDetailType(type);
    setIsDetailModalOpen(true);
  };

  // Validation du formulaire
  const validateForm = () => {
    const errors = {};
    
    if (!formData.nom.trim()) {
      errors.nom = "Le nom est requis";
    }
    if (!formData.description.trim()) {
      errors.description = "La description est requise";
    }
    if (!formData.duree_minutes || formData.duree_minutes < 30) {
      errors.duree_minutes = "La durée doit être d'au moins 30 minutes";
    }
    if (!formData.nombre_jury_min || formData.nombre_jury_min < 2) {
      errors.nombre_jury_min = "Le nombre minimum de jury doit être d'au moins 2";
    }
    if (!formData.nombre_jury_max || formData.nombre_jury_max < formData.nombre_jury_min) {
      errors.nombre_jury_max = "Le nombre maximum doit être supérieur au minimum";
    }
    if (!formData.niveau_requis) {
      errors.niveau_requis = "Le niveau requis est requis";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Soumettre le formulaire
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const dataToSend = {
        ...formData,
        duree_minutes: parseInt(formData.duree_minutes),
        nombre_jury_min: parseInt(formData.nombre_jury_min),
        nombre_jury_max: parseInt(formData.nombre_jury_max)
      };

      if (selectedType) {
        // Mise à jour
        await api.put(`/soutenance/types-soutenance/${selectedType.id}/`, dataToSend);
        toast.success("Type de soutenance mis à jour avec succès");
      } else {
        // Création
        await api.post('/soutenance/types-soutenance/', dataToSend);
        toast.success("Type de soutenance créé avec succès");
      }

      fetchTypes();
      setIsModalOpen(false);
    } catch (error) {
      console.error("Erreur lors de l'opération:", error);
      
      let errorMsg = "Une erreur est survenue. Veuillez réessayer.";
      if (error.response && error.response.data) {
        const responseData = error.response.data;
        if (typeof responseData === 'object') {
          const errorMessages = Object.entries(responseData)
            .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
            .join('\n');
          errorMsg = errorMessages || errorMsg;
        } else if (typeof responseData === 'string') {
          errorMsg = responseData;
        }
      }
      toast.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  // Gestion de la suppression
  const handleOpenDeleteModal = (type) => {
    setTypeToDelete(type);
    setConfirmConfig({
      title: "Supprimer le type de soutenance",
      message: `Êtes-vous sûr de vouloir supprimer le type de soutenance "${type.nom}" ?`,
      confirmText: "Supprimer",
      type: "danger"
    });
    setIsConfirmModalOpen(true);
  };

  const handleDelete = async () => {
    if (!typeToDelete) return;
    
    setDeleteLoading(true);
    try {
      await api.delete(`/soutenance/types-soutenance/${typeToDelete.id}/`);
      toast.success("Type de soutenance supprimé avec succès");
      fetchTypes();
    } catch (error) {
      console.error("Erreur lors de la suppression:", error);
      toast.error("Impossible de supprimer ce type de soutenance. Veuillez réessayer.");
    } finally {
      setDeleteLoading(false);
      setIsConfirmModalOpen(false);
    }
  };

  // Fonction utilitaire pour formater la durée
  const formatDuree = (minutes) => {
    const heures = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return heures > 0 ? `${heures}h${mins > 0 ? mins.toString().padStart(2, '0') : ''}` : `${mins}min`;
  };

  // Rendering du footer du modal de création/modification
  const renderModalFooter = () => (
    <div className="flex justify-end space-x-4">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsModalOpen(false)}
      >
        Annuler
      </button>
      <button
        type="button"
        className={`inline-flex justify-center px-6 py-2.5 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md shadow-sm hover:bg-[#161c3f] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654] ${submitting ? 'opacity-75 cursor-not-allowed' : ''}`}
        onClick={handleSubmit}
        disabled={submitting}
      >
        {submitting ? (
          <>
            <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            Traitement...
          </>
        ) : (
          selectedType ? 'Mettre à jour' : 'Créer'
        )}
      </button>
    </div>
  );

  // Rendering du footer du modal de détails
  const renderDetailModalFooter = () => (
    <div className="flex justify-end">
      <button
        type="button"
        className="inline-flex justify-center px-6 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        onClick={() => setIsDetailModalOpen(false)}
      >
        Fermer
      </button>
    </div>
  );

  if (loading && types.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
          <input
            type="text"
            placeholder="Rechercher un type de soutenance..."
            value={searchTerm}
            onChange={handleSearchChange}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <button
          onClick={() => handleOpenModal()}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#202654] hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#202654]"
        >
          <Plus className="h-5 w-5 mr-2" />
          Nouveau Type
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {types.length === 0 && !loading ? (
        <EmptyState
          icon={Settings}
          title="Aucun type de soutenance"
          description="Aucun type de soutenance trouvé."
          actionLabel="Créer un type"
          onAction={() => handleOpenModal()}
        />
      ) : (
        <div className="bg-white shadow-sm rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type de soutenance
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Durée
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Jury
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Niveau
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
          {types.map((type) => (
                  <tr key={type.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-lg bg-purple-100 flex items-center justify-center">
                            <Settings className="h-5 w-5 text-purple-600" />
                          </div>
                        </div>
                        <div className="ml-4 min-w-0 flex-1">
                          <div className="text-sm font-medium text-gray-900 truncate">{type.nom}</div>
                          {type.description && (
                            <div className="text-sm text-gray-500 truncate">{type.description}</div>
                          )}
                        </div>
                    </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        <Clock className="h-3 w-3 mr-1" />
                        {formatDuree(type.duree_minutes)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        <Users className="h-3 w-3 mr-1" />
                        {type.nombre_jury_min} - {type.nombre_jury_max}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        {type.niveau_requis}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex justify-end space-x-2">
                        <button
                          onClick={() => handleOpenDetailModal(type)}
                          className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50 transition-colors"
                          title="Voir les détails"
                        >
                          <Eye className="h-5 w-5" />
                        </button>
                    <button
                          onClick={() => handleOpenModal(type)}
                          className="text-indigo-600 hover:text-indigo-900 p-1 rounded-full hover:bg-indigo-50 transition-colors"
                          title="Modifier"
                    >
                          <Edit2 className="h-5 w-5" />
                    </button>
                    <button
                          onClick={() => handleOpenDeleteModal(type)}
                          className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50 transition-colors"
                          title="Supprimer"
                    >
                          <Trash2 className="h-5 w-5" />
                    </button>
                  </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
                </div>

          {pagination && pagination.total_pages > 1 && (
            <Pagination
              pagination={pagination}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          )}
        </div>
      )}

      {/* Modal de création/modification */}
      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={selectedType ? 'Modifier le type de soutenance' : 'Nouveau type de soutenance'}
        footer={renderModalFooter()}
        maxWidth="max-w-2xl"
      >
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <FormInput
              label="Nom du type"
              type="text"
              name="nom"
              value={formData.nom}
              onChange={handleInputChange}
              placeholder="Ex: Mémoire de Master"
              required
              error={formErrors.nom}
              icon={Settings}
            />
            
            <FormInput
              label="Niveau requis"
              type="select"
              name="niveau_requis"
              value={formData.niveau_requis}
              onChange={handleInputChange}
              options={[
                { value: '', label: 'Sélectionner un niveau' },
                ...niveauxRequis
              ]}
              required
              error={formErrors.niveau_requis}
            />
          </div>

          <FormInput
            label="Description"
            type="textarea"
            name="description"
            value={formData.description}
            onChange={handleInputChange}
            placeholder="Description détaillée du type de soutenance"
            required
            error={formErrors.description}
            rows={3}
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <FormInput
              label="Durée (minutes)"
              type="number"
              name="duree_minutes"
              value={formData.duree_minutes}
              onChange={handleInputChange}
              placeholder="120"
              required
              error={formErrors.duree_minutes}
              min="30"
              max="480"
              icon={Clock}
            />

            <FormInput
              label="Jury min"
              type="number"
              name="nombre_jury_min"
              value={formData.nombre_jury_min}
              onChange={handleInputChange}
              required
              error={formErrors.nombre_jury_min}
              min="2"
              max="15"
              icon={Users}
            />

            <FormInput
              label="Jury max"
              type="number"
              name="nombre_jury_max"
              value={formData.nombre_jury_max}
              onChange={handleInputChange}
              required
              error={formErrors.nombre_jury_max}
              min="3"
              max="20"
              icon={Users}
            />
          </div>

          <div className="text-sm text-gray-500 mt-4">
            <span className="text-red-500">*</span> Champs obligatoires
        </div>
        </form>
      </Modal>

      {/* Modal de détails */}
      <Modal
        isOpen={isDetailModalOpen}
        onClose={() => setIsDetailModalOpen(false)}
        title="Détails du type de soutenance"
        footer={renderDetailModalFooter()}
        maxWidth="max-w-2xl"
      >
        {detailType && (
          <div className="space-y-6">
            {/* Informations générales */}
            <div className="bg-gray-50 p-6 rounded-lg">
              <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
                <Settings className="h-5 w-5 mr-2 text-purple-600" />
                Informations générales
            </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nom du type</label>
                  <p className="mt-1 text-sm text-gray-900">{detailType.nom}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Niveau requis</label>
                  <p className="mt-1 text-sm text-gray-900">{detailType.niveau_requis}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Durée</label>
                  <p className="mt-1 text-sm text-gray-900">{formatDuree(detailType.duree_minutes)}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nombre de jurés</label>
                  <p className="mt-1 text-sm text-gray-900">{detailType.nombre_jury_min} - {detailType.nombre_jury_max} membres</p>
                </div>
                </div>
              </div>

            {/* Description */}
            {detailType.description && (
              <div className="bg-blue-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Description</h3>
                <p className="text-sm text-gray-900 whitespace-pre-wrap">{detailType.description}</p>
              </div>
            )}

            {/* Critères d'évaluation */}
            {detailType.criteres && detailType.criteres.length > 0 && (
              <div className="bg-green-50 p-6 rounded-lg">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Critères d'évaluation</h3>
                <div className="space-y-2">
                  {detailType.criteres.map((critere, index) => (
                    <div key={index} className="flex justify-between items-center p-2 bg-white rounded border">
                      <span className="text-sm font-medium">{critere.nom}</span>
                      <span className="text-xs text-gray-500">Coeff: {critere.coefficient}</span>
                    </div>
                  ))}
          </div>
        </div>
      )}
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <ConfirmationModal 
        isOpen={isConfirmModalOpen}
        onClose={() => setIsConfirmModalOpen(false)}
        onConfirm={handleDelete}
        title={confirmConfig.title || "Supprimer le type de soutenance"}
        message={confirmConfig.message || "Êtes-vous sûr de vouloir supprimer ce type de soutenance ?"}
        confirmText={confirmConfig.confirmText || "Supprimer"}
        cancelText="Annuler"
        type={confirmConfig.type || "danger"}
        isLoading={deleteLoading}
      />
    </div>
  );
};

export default TypesSoutenanceTab; 